import React, { useCallback, useEffect, useState } from 'react'
import { auditService as defaultAuditService } from '@/services/auditService'
import type { AuditAction, AuditFilter, AuditLog, AuditSeverity } from '@/types/auditLog'

export interface AdminAuditLogPageProps {
  service?: Pick<typeof defaultAuditService, 'getAuditLogs'>
}

const ACTIONS: AuditAction[] = [
  'CREATE',
  'UPDATE',
  'DELETE',
  'APPROVE',
  'REJECT',
  'LOGIN',
  'LOGOUT',
  'PASSWORD_RESET',
  'CERTIFICATE_REVOKE',
  'GRADE_OVERRIDE',
  'COMMISSION_PAYOUT',
  'REFUND_PROCESSED',
  'PAYMENT_PROCESSED',
  'EXPORT',
  'READ',
  // M3 / L6: expose the content-authoring + privilege + identity actions so
  // admins can filter for them in the audit inbox.
  'SUBMIT_FOR_REVIEW',
  'APPROVE_LESSON',
  'REJECT_LESSON',
  'REQUEST_DELETE',
  'APPROVE_DELETE',
  'GRANT_PERMISSIONS',
  'REVOKE_PERMISSIONS',
  'CHANGE_EMAIL',
]

const SEVERITIES: AuditSeverity[] = ['info', 'warning', 'critical']

/**
 * Admin page that surfaces audit_logs entries. Filter bar supports date range,
 * actor id, action, resource type, and severity. Each row expands to show the
 * full metadata JSON.
 */
export const AdminAuditLogPage: React.FC<AdminAuditLogPageProps> = ({
  service = defaultAuditService,
}) => {
  const [logs, setLogs] = useState<AuditLog[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const [filter, setFilter] = useState<AuditFilter>({ limit: 50, offset: 0 })

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const rows = await service.getAuditLogs(filter)
      setLogs(rows || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load audit logs')
    } finally {
      setLoading(false)
    }
  }, [service, filter])

  useEffect(() => {
    load()
  }, [load])

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleFilterChange = <K extends keyof AuditFilter>(key: K, value: AuditFilter[K]) => {
    setFilter((prev) => ({ ...prev, [key]: value, offset: 0 }))
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">Audit Logs</h1>
        <p className="text-gray-500 dark:text-text-muted text-sm">
          Immutable record of critical operations. Requires `audit.view` permission.
        </p>
      </div>

      <div className="mb-4 grid grid-cols-1 md:grid-cols-5 gap-3">
        <input
          type="date"
          aria-label="Start date"
          onChange={(e) =>
            handleFilterChange('startTime', e.target.value ? `${e.target.value}T00:00:00Z` : undefined)
          }
          className="rounded-lg bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 px-3 py-2 text-sm text-gray-900 dark:text-white"
          data-testid="filter-start"
        />
        <input
          type="date"
          aria-label="End date"
          onChange={(e) =>
            handleFilterChange('endTime', e.target.value ? `${e.target.value}T23:59:59Z` : undefined)
          }
          className="rounded-lg bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 px-3 py-2 text-sm text-gray-900 dark:text-white"
          data-testid="filter-end"
        />
        <input
          type="text"
          aria-label="Actor id"
          placeholder="Actor id"
          onChange={(e) => handleFilterChange('actorId', e.target.value || undefined)}
          className="rounded-lg bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 px-3 py-2 text-sm text-gray-900 dark:text-white"
          data-testid="filter-actor"
        />
        <select
          aria-label="Action"
          onChange={(e) =>
            handleFilterChange('action', (e.target.value || undefined) as AuditAction | undefined)
          }
          className="rounded-lg bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 px-3 py-2 text-sm text-gray-900 dark:text-white"
          data-testid="filter-action"
        >
          <option value="">All actions</option>
          {ACTIONS.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
        <select
          aria-label="Severity"
          onChange={(e) =>
            handleFilterChange(
              'severity',
              (e.target.value || undefined) as AuditSeverity | undefined,
            )
          }
          className="rounded-lg bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 px-3 py-2 text-sm text-gray-900 dark:text-white"
          data-testid="filter-severity"
        >
          <option value="">All severities</option>
          {SEVERITIES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {loading && <p className="text-gray-500 dark:text-text-muted" data-testid="audit-loading">Loading…</p>}
      {error && (
        <div role="alert" className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
          {error}
        </div>
      )}

      {!loading && !error && logs.length > 0 ? (
        <div className="rounded-lg bg-white dark:bg-surface-card border border-slate-100 dark:border-white/5 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[500px]" data-testid="audit-table">
              <thead>
                <tr className="text-left text-xs text-slate-500 dark:text-text-muted uppercase tracking-wider border-b border-slate-100 dark:border-white/5 bg-slate-50 dark:bg-white/5">
                  <th className="px-5 py-4 font-bold">Time</th>
                  <th className="px-5 py-4 font-bold">Actor</th>
                  <th className="px-5 py-4 font-bold">Action</th>
                  <th className="px-5 py-4 font-bold">Resource</th>
                  <th className="px-5 py-4 font-bold hidden md:table-cell">Severity</th>
                  <th className="px-5 py-4 font-bold"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                {logs.map((log) => {
                  const isOpen = expanded.has(log.id)
                  return (
                    <React.Fragment key={log.id}>
                      <tr className="hover:bg-slate-50 dark:hover:bg-white/5 transition-colors" data-testid={`audit-row-${log.id}`}>
                        <td className="px-5 py-4 text-xs text-slate-500 dark:text-text-muted">
                          {new Date(log.createdAt).toLocaleString()}
                        </td>
                        <td className="px-5 py-4 text-sm font-medium text-slate-900 dark:text-white">{log.actorEmail}</td>
                        <td className="px-5 py-4 text-sm font-bold text-primary">{log.action}</td>
                        <td className="px-5 py-4 text-sm text-slate-500 dark:text-text-muted">
                          {log.resourceType}: {log.resourceDisplay || log.resourceId.slice(0, 8)}
                        </td>
                        <td className="px-5 py-4 text-xs hidden md:table-cell">
                          <span
                            className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${
                              log.severity === 'critical'
                                ? 'bg-red-500/10 text-red-400'
                                : log.severity === 'warning'
                                  ? 'bg-amber-500/10 text-amber-400'
                                  : 'bg-slate-500/10 text-slate-500 dark:text-slate-400'
                            }`}
                          >
                            {log.severity}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-right">
                          <button
                            type="button"
                            onClick={() => toggleExpand(log.id)}
                            className="size-8 rounded-lg bg-slate-100 dark:bg-white/5 flex items-center justify-center hover:bg-slate-200 dark:hover:bg-white/10 transition-colors"
                            data-testid={`audit-expand-${log.id}`}
                          >
                            <span className="material-symbols-outlined text-[18px]">
                              {isOpen ? 'expand_less' : 'expand_more'}
                            </span>
                          </button>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr data-testid={`audit-details-${log.id}`}>
                          <td colSpan={6} className="px-5 py-6 bg-slate-50/50 dark:bg-white/[0.02]">
                            <div className="text-xs text-slate-500 dark:text-text-muted space-y-4">
                              <div className="flex gap-6">
                                <div>
                                  <span className="font-bold uppercase tracking-wider text-[10px] block mb-1">IP Address</span>
                                  <span className="font-mono">{log.ipAddress || '-'}</span>
                                </div>
                                <div>
                                  <span className="font-bold uppercase tracking-wider text-[10px] block mb-1">User Agent</span>
                                  <span className="font-mono truncate max-w-md block">{log.userAgent || '-'}</span>
                                </div>
                              </div>
                              <div>
                                <span className="font-bold uppercase tracking-wider text-[10px] block mb-2">Metadata JSON</span>
                                <pre className="p-4 rounded-lg bg-slate-900 dark:bg-black/40 text-[11px] text-slate-300 dark:text-slate-300 overflow-auto border border-slate-800">
                                  {JSON.stringify(
                                    {
                                      metadata: (log as AuditLog & { metadata?: unknown }).metadata ?? {},
                                      old_values: log.oldValues ?? null,
                                      new_values: log.newValues ?? null,
                                    },
                                    null,
                                    2,
                                  )}
                                </pre>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        !loading && !error && (
          <div className="p-12 text-center rounded-lg bg-white dark:bg-surface-card border border-slate-200 dark:border-white/5 border-dashed text-slate-500 dark:text-text-muted">
            <span className="material-symbols-outlined text-4xl mb-2 opacity-20">history</span>
            <p className="font-medium">No entries match the current filter.</p>
          </div>
        )
      )}
    </div>
  )
}

export default AdminAuditLogPage
